import React, { useEffect, useState } from 'react';
import type { FormikValues } from 'formik';
import { useFormikContext } from 'formik';
import { get, isEmpty, isNil } from 'lodash';

import { Text } from '~/components/core';
import JsonEditor from '~/components/core/Molecules/JsonEditor';

import copies from '../copies.json';

const TEXTS = copies.field_groups;

interface StaticTreeFieldProps {
  /** Formik path of the stored tree. */
  staticJsonPath: string;
  disabled: boolean;
  /**
   * Told the reason the document on screen could not be stored, or `undefined` once it could - so
   * the dialog holding this field can refuse to apply a tree that no longer matches the editor.
   */
  onDocumentErrorChange: (documentError: string | undefined) => void;
}

/** The pasted tree, edited in the shared JSON editor and stored as the object every consumer reads. */
const StaticTreeField: React.FC<StaticTreeFieldProps> = ({ staticJsonPath, disabled, onDocumentErrorChange }) => {
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  const storedTree = get(values, staticJsonPath);
  // Two independent signals, not one message, for the reason the sibling mock-policy dialog
  // documents: text that is not JSON arrives through `onValidationError`, while an emptied document
  // and a parseable array or scalar are reported there as *zero* errors and only show up in
  // `onChange`. Folding them into one piece of state lets the second report clear the first.
  const [hasParseError, setHasParseError] = useState(false);
  const [hasNonTreeDocument, setHasNonTreeDocument] = useState(false);

  const onDocumentChange = (value: unknown) => {
    // The editor reports `null` once its text is emptied, and an array or a scalar is valid JSON
    // too - but none of the three is a tree. Storing `null` in particular reads as "no static source",
    // which unmounts this field and swaps the dialog back to the endpoint editor mid-edit, so the
    // stored tree is held and the reason is said out loud instead.
    if (isNil(value) || typeof value !== 'object' || Array.isArray(value)) {
      setHasNonTreeDocument(true);
      return;
    }
    setHasNonTreeDocument(false);
    setFieldValue(staticJsonPath, value);
  };

  // Text that does not parse at all comes first: until it does, what it would have meant is unknown.
  const documentError = hasParseError
    ? TEXTS.static_json_invalid_error
    : hasNonTreeDocument
      ? TEXTS.static_json_not_an_object_error
      : undefined;

  // Cleared on unmount too: switching the source to an endpoint unmounts this field, and a reason
  // about a tree that is no longer there must not keep holding the dialog back.
  useEffect(() => {
    onDocumentErrorChange(documentError);
    return () => onDocumentErrorChange(undefined);
  }, [documentError, onDocumentErrorChange]);

  return (
    <div className="flex flex-col gap-8">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
        {TEXTS.static_json_label}
      </Text>
      <div className="h-[320px] overflow-hidden rounded-xl border border-slate-300">
        {/* Every option but the callbacks is read once, when the editor mounts (see the component's
            own contract), so the read-only state is a remount rather than a prop change. The tree is
            re-seeded from Formik, which holds the last document that parsed as an object. */}
        <JsonEditor
          key={disabled ? 'view' : 'edit'}
          defaultValue={storedTree ?? {}}
          mode={disabled ? 'view' : 'tree'}
          allowedModes={disabled ? ['view', 'text'] : ['tree', 'text']}
          onChange={onDocumentChange}
          onValidationError={(errors) => setHasParseError(!isEmpty(errors))}
          htmlElementProps={{ className: 'h-[320px] [&_.jsoneditor]:overflow-hidden [&_.jsoneditor]:rounded-xl' }}
        />
      </div>
      <Text
        variant={Text.VARIANTS.XS}
        colorVariant={isNil(documentError) ? Text.COLOR_VARIANTS.SECONDARY : Text.COLOR_VARIANTS.ERROR}
      >
        {documentError ?? TEXTS.static_json_note}
      </Text>
    </div>
  );
};

export default StaticTreeField;
